import React, { useCallback, useContext, useEffect, useState } from 'react'
import InlineTip from 'app/React/Components/misc/InlineTip'
import { getCurrentPrice, getSecurityDeposit } from 'app/React/Helpers/PriceHelper'
import Price from 'app/React/Components/Footer/Price'
import PropTypes from 'prop-types'
import '@reach/tooltip/styles.css'
import { usePopper } from 'react-popper'
import '../style.scss'
import IsNotDealerConfigurator from 'app/React/Components/misc/IsNotDealerConfigurator'
import SettingContextStep3 from "app/React/Components/Form/Step3/SettingContextStep3"
import { sameWidth } from 'app/React/Helpers/OptionHelper'
import { ConfiguratorContext } from "app/React/ConfiguratorContainer";

export default function OptionStep3(props) {
  const {
    cartItems,
    currentEngineTransmission,
    currentVariant,
    identifier,
    order,
    strings,
    setInteriorImage,
    setOrder,
    defaultValidateSteps
  } = useContext(ConfiguratorContext)

  const {
    markExcluded,
    markNotExcluded,
    isExcluded,
    getCheckedForSetting,
    isDisabled,
    isChecked,
    markChecked,
    markUnchecked,
    getChecked
  } = React.useContext(SettingContextStep3)

  const [hasBeenLoaded, setHasBeenLoaded] = useState(false)

  const inDepositLabel = strings.footer?.in_security_deposit_leasing ?? 'i udbetaling'

  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const [arrowElement, setArrowElement] = useState(null)

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'left',
    strategy: 'fixed',
    modifiers: [
      { name: 'preventOverflow', options: { padding: 16 } },
      {
        name: 'offset',
        options: {
          offset: [0, 8]
        }
      },
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'top'],
          padding: { bottom: 80 }
        }
      },
      sameWidth
    ]
  })

  const fromString = strings.misc?.from ?? 'From'
  const depositLabel = strings.misc?.security_deposit ?? 'Udbetaling'

  const option = props.option
  const inputName = 'option' + props.settingid
  const inputValue = option.id

  const disabled = isDisabled(props.option.id)
  const hidden = option.hide_on === order.paymentType || option.hide_on === 'all'

  useEffect(() => {
    if (!hasBeenLoaded) {
      return
    }

    const isRequired = props.required && props.required !== '0'
    if (isRequired) {
      return
    }

    if (!isChecked(props.option.id)) {
      order.configuration = updateConfiguration(true)
      setOrder({ ...order })

      setInteriorImage(null)
      updateValue(true)
    } else {
      order.configuration = updateConfiguration(false)
      setOrder({ ...order })

      const extraEquipments = currentVariant.steps?.[3].settings?.[props.settingid]?.options ?? {}
      const extraEquipment = extraEquipments.find(extraEquipment => extraEquipment.id == props.option.id) ?? null

      setInteriorImage(extraEquipment)
      updateValue(false)
    }
  }, [isChecked(props.option.id)])

  const onClick = function () {
    const isRequired = props.required && props.required !== '0'
    if (isChecked(props.option.id)) {
      // This setting is required so don't de-select it
      if (isRequired) {
        return
      }

      markUnchecked(props.option.id, props.option.option_selectable_rules_option)
      markNotExcluded(props.option.id)
    } else {
      markExcluded(props.option.id, props.option.option_selectable_rules_option)
      markChecked(props.option.id, props.settingid, isRequired)

      if (isRequired) {
        order.configuration = updateConfiguration(updateConfiguration(false, [props.option.id]))
        setOrder({ ...order })

        const extraEquipment = currentVariant.steps?.[3].settings?.[props.settingid]?.options?.[props.option.id] ?? null
        setInteriorImage(extraEquipment)
      }
    }
  }

  function updateConfiguration(shouldDelete, checkedForSettings = null) {
    const updatedConfiguration = { ...order.configuration }

    if (shouldDelete) {
      if (updatedConfiguration?.extra_equipments.hasOwnProperty(props.settingid)) {
        if (updatedConfiguration?.extra_equipments[props.settingid]?.length <= 1) {
          const {
            [props.settingid]: _,
            ...newUpdatedConfigurationExtraEquipments
          } = updatedConfiguration?.extra_equipments
          updatedConfiguration.extra_equipments = newUpdatedConfigurationExtraEquipments
        } else {
          const index = updatedConfiguration?.extra_equipments[props.settingid]?.indexOf(props.option.id)
          if (index > -1) {
            updatedConfiguration?.extra_equipments[props.settingid].splice(index, 1)
          }
        }
      }
    } else {
      const settingid = String(props.settingid)
      checkedForSettings = checkedForSettings ?? getCheckedForSetting(settingid)

      updatedConfiguration.extra_equipments = { ...updatedConfiguration.extra_equipments, ...{ [settingid]: checkedForSettings } }
    }

    order.configuration = updatedConfiguration
    setOrder({ ...order })

    return updatedConfiguration
  }

  const updateValue = useCallback(
    function (shouldDelete, option = props.option) {
      if (hidden) {
        return
      }

      if (typeof shouldDelete !== 'boolean') {
        shouldDelete = false
      }

      if (typeof props.onChange === 'function' && !shouldDelete) {
        const shouldStillUpdate = props.onChange(props.settingid, option)

        if (shouldStillUpdate !== true) {
          return
        }
      }

      order.price = getCurrentPrice(order.paymentType, order, currentVariant, cartItems)
      order.security_deposit = getSecurityDeposit(order.paymentType, order, currentVariant, currentEngineTransmission, cartItems)

      setOrder({ ...order })

      defaultValidateSteps()
    },
    [
      order.configuration,
      hidden,
      identifier,
      order,
      order.paymentType,
      props,
      setOrder,
      defaultValidateSteps
    ]
  )

  const htmlId = 'option' + props.settingid + '_' + props.option.id + (props.htmlIdPostfix ? '_' + props.htmlIdPostfix : '')

  const displayPrice = function () {
    if (order.paymentType !== 'leasing' || option.use_leasing_price === '0') {
      if (typeof props.option.price !== 'undefined') {
        return (
          <IsNotDealerConfigurator>
            {props.option.price > 0 && (<span>+</span>)}
            <Price price={props.option.price} format='cash'/>
          </IsNotDealerConfigurator>
        )
      }
    } else {
      if (typeof props.option.price_leasing !== 'undefined') {
        return (
          <IsNotDealerConfigurator>
            <div>
              {props.option.price_leasing > 0 && (<span>+</span>)}
              <Price price={props.option.price_leasing}/>
            </div>

            {
              typeof props.option.security_deposit_leasing !== 'undefined' && props.option.security_deposit_leasing > 0 && (
                <div>
                  <span>+</span>
                  <Price price={props.option.security_deposit_leasing} format='cash'/>
                  <span>{inDepositLabel}</span>
                </div>
              )
            }
          </IsNotDealerConfigurator>
        )
      }
    }
    return null
  }

  useEffect(() => {
    setHasBeenLoaded(true)
  }, [])

  // endregion
  return (
    <>
      <input
        id={htmlId}
        type={props.required && props.required !== '0' ? 'radio' : 'checkbox'}
        name={inputName}
        value={inputValue}
        checked={isChecked(props.option.id)}
        data-checked={isChecked(props.option.id)}
        onChange={updateValue}
        onClick={onClick}
        disabled={disabled}
        required={props.required}
      />
      <label
        ref={setReferenceElement}
        htmlFor={htmlId}
        className='option'
        layout={props.layout}
        data-disabled={disabled}
      >
        {option.color_hex.substr(0, 1) === '#' ? (
          <span className='color' style={{ backgroundColor: option.color_hex }}/>
        ) : null}
        {typeof option.image_url === 'string' && option.image_url.length >= 0 ? (
          <img className='image' src={option.image_url} alt=''/>
        ) : null}
        <div>{option.title?.length > 0 ? option.title : option.name}</div>
        <div className='description'>
          {option.description_override ? (
            <span dangerouslySetInnerHTML={{ __html: option.description_override }}/>
          ) : (
            <>
              {displayPrice()}
              {option?.description_title?.length > 0 && option?.description?.length > 0 ? (
                <InlineTip button='Ok' label={option.description_title} content={option.description}/>
              ) : null}
              {option?.description_title?.length > 0 && !option?.description?.length > 0 ? (
                <span dangerouslySetInnerHTML={{ __html: option.description_title }}/>
              ) : null}
            </>
          )}
        </div>
      </label>
      {disabled && option.tooltip_unselectable ? (
        <div className='tooltip2' ref={setPopperElement} style={styles.popper} {...attributes.popper}>
          <div dangerouslySetInnerHTML={{ __html: option.tooltip_unselectable }}/>
          <div className='arrow' ref={setArrowElement} style={styles.arrow}/>
        </div>
      ) : null}
    </>
  )
}

OptionStep3.defaultProps = {
  onChange: null,
  hide_on: 'none'
}
OptionStep3.propTypes = {
  layout: PropTypes.any,
  checked: PropTypes.any,
  settingid: PropTypes.any,
  htmlIdPostfix: PropTypes.any,
  setChecked: PropTypes.any,
  onChange: PropTypes.any,
  required: PropTypes.any,

  option: PropTypes.shape({
    id: PropTypes.any,
    name: PropTypes.any,
    color_hex: PropTypes.any,
    image_url: PropTypes.any,
    min_price_leasing: PropTypes.any,
    security_deposit_leasing: PropTypes.any,
    variant_index: PropTypes.any,
    price: PropTypes.any,
    min_price: PropTypes.any,
    use_leasing_price: PropTypes.any,
    option_selectable_rules_option: PropTypes.any,
    option_selectable_rules_is_selected: PropTypes.any,
    hide_on: PropTypes.any,
    price_leasing: PropTypes.any,
    title: PropTypes.any,
    description: PropTypes.any,
    description_override: PropTypes.any,
    description_title: PropTypes.any
  })
}
